import { useMemo } from 'react';
import type { AppPortal, AccessibleSchool, RoleContext, RoleEnum } from '~/types';

export function useRoleContext(
  bootstrapData: AppPortal | undefined | null,
  selectedSchoolId: string | null,
  selectedRole: string | null,
  selectedSchoolYearId: string | null
): RoleContext {
  return useMemo(() => {
    if (!bootstrapData?.accessibleSchools || !selectedSchoolId || !selectedRole) {
      return {
        selectedSchool: null,
        selectedRole: null,
        selectedSchoolYear: null,
        accessibleSchools: [],
        canAccess: () => false,
      };
    }

    // Find selected school
    const accessibleSchool = bootstrapData.accessibleSchools.find(
      (as: AccessibleSchool) => as.school.id === selectedSchoolId
    );

    if (!accessibleSchool) {
      return {
        selectedSchool: null,
        selectedRole: null,
        selectedSchoolYear: null,
        accessibleSchools: bootstrapData.accessibleSchools || [],
        canAccess: () => false,
      };
    }

    const selectedSchool = accessibleSchool.school;

    // Find selected school year
    let selectedSchoolYear = null;
    if (selectedSchoolYearId) {
      const accessibleYear = accessibleSchool.accessibleSchoolYears.find(
        (asy) => asy.schoolYear.id === selectedSchoolYearId && asy.roles.includes(selectedRole as RoleEnum)
      );
      if (accessibleYear) {
        selectedSchoolYear = accessibleYear.schoolYear;
      }
    }

    // Permission check helper
    const canAccess = (requiredRole: string, schoolId?: string, schoolYearId?: string) => {
      // Check school-level access
      if (schoolId && schoolId !== selectedSchoolId) {
        const targetSchool = bootstrapData.accessibleSchools.find((as: AccessibleSchool) => as.school.id === schoolId);
        if (!targetSchool || !targetSchool.roles.includes(requiredRole as RoleEnum)) {
          return false;
        }
      } else {
        // Check current school
        if (!accessibleSchool.roles.includes(requiredRole as RoleEnum)) {
          return false;
        }
      }

      // Check school year-level access if specified
      if (schoolYearId) {
        const targetSchool = schoolId ?
          bootstrapData.accessibleSchools.find((as: AccessibleSchool) => as.school.id === schoolId) :
          accessibleSchool;

        if (targetSchool) {
          const targetYear = targetSchool.accessibleSchoolYears.find(
            (asy) => asy.schoolYear.id === schoolYearId && asy.roles.includes(requiredRole as RoleEnum)
          );
          if (!targetYear) {
            return false;
          }
        }
      }

      return true;
    };

    return {
      selectedSchool,
      selectedRole,
      selectedSchoolYear,
      accessibleSchools: bootstrapData.accessibleSchools || [],
      canAccess,
    };
  }, [bootstrapData, selectedSchoolId, selectedRole, selectedSchoolYearId]);
}
